import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import { MARKETING_CONSENT_FORM_NAME, MARKETING_SETTINGS_MESSAGE_CONTEXT } from "@obg/ngx.common";
import { AbstractTranslatableSnackBarService, MessageService, MessageType } from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { TelemetryService } from "@obg/ngx.global.evo";
import { Subscription } from "rxjs";
import { switchMap, take } from "rxjs/operators";

import { MarketingConsentRenewalData, MarketingConsentRenewalForm } from "./marketing-consent-renewal.model";

@Component({
	selector: "obg-m-marketing-consent-renewal",
	templateUrl: "./marketing-consent-renewal.component.html",
	styleUrls: ["./marketing-consent-renewal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class MarketingConsentRenewalComponent implements OnInit, OnDestroy {
	form: FormGroup<MarketingConsentRenewalForm>;

	@HostBinding("class.obg-m-marketing-consent-renewal") hostCssClass = true;
	@HostBinding("attr.test-id") testId = "marketing-consent-renewal";
	@HostBinding("attr.data-test-id") dataTestId = "marketing-consent-renewal";

	private brandMarketingValueChanges$$: Subscription | undefined;
	private marketingSettingsMessage$$: Subscription | undefined;
	private isDialogConfirmed = false;

	get phoneNumber(): string {
		return `${this.data.subscriptions.phoneExtension ?? ""}${this.data.subscriptions?.phoneNumber ?? ""}`;
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: MarketingConsentRenewalData,
		private dialogRef: MatDialogRef<MarketingConsentRenewalComponent>,
		private fb: FormBuilder,
		private messageService: MessageService,
		private snackbarService: AbstractTranslatableSnackBarService,
		private telemetryService: TelemetryService
	) {
		this.form = this.fb.group({
			consents: this.fb.group({
				brandMarketing: this.fb.control(!!this.data.consents.brandMarketing, { nonNullable: true }),
				groupMarketing: this.fb.control(!!this.data.consents.groupMarketing, { nonNullable: true }),
			}),
			subscriptions: this.fb.group({
				acceptEmailOffers: this.fb.control(!!this.data.subscriptions?.acceptEmailOffers, { nonNullable: true }),
				acceptSmsOffers: this.fb.control(!!this.data.subscriptions?.acceptSmsOffers, { nonNullable: true }),
				acceptTelesalesOffers: this.fb.control(!!this.data.subscriptions?.acceptTelesalesOffers, {
					nonNullable: true,
				}),
			}),
		});

		this.telemetryService.track({
			event: GtmEventAction.GenericFormOpen,
			FormName: MARKETING_CONSENT_FORM_NAME,
		});
	}

	ngOnInit() {
		this.marketingSettingsMessage$$ = this.messageService
			.consume$({
				context: [MARKETING_SETTINGS_MESSAGE_CONTEXT.consents],
			})
			.pipe(
				switchMap(([message]) =>
					this.snackbarService.openSnackBar$(
						{
							message:
								message.type === MessageType.Success
									? "marketing-settings.consents.m_update_consent_success"
									: "general.generic-error",
							action: "general.close",
							duration: 4000,
							type: message.type,
						},
						[TranslationGroup.Common]
					)
				),
				take(1)
			)
			.subscribe();

		if (!this.data.consents.brandMarketing) {
			this.disableGroupMarketingAndSubscriptions();
		}

		this.brandMarketingValueChanges$$ = this.form.controls.consents.controls.brandMarketing.valueChanges.subscribe(
			(enabled) => {
				if (enabled) {
					this.form.controls.consents.controls.groupMarketing.enable({ emitEvent: false });
					this.form.controls.subscriptions.enable({ emitEvent: false });
					return;
				}

				this.disableGroupMarketingAndSubscriptions();
			}
		);
	}

	ngOnDestroy() {
		this.brandMarketingValueChanges$$?.unsubscribe();

		if (!this.isDialogConfirmed) {
			this.marketingSettingsMessage$$?.unsubscribe();
		}
	}

	onConfirm() {
		this.isDialogConfirmed = true;
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSubmit,
			FormName: MARKETING_CONSENT_FORM_NAME,
			Label: this.getGtmLabelOnSubmit(),
		});
		this.dialogRef.close(this.form.value);
	}

	onCancel() {
		this.telemetryService.track({
			FormName: MARKETING_CONSENT_FORM_NAME,
		});
		this.dialogRef.close(undefined);
	}

	private disableGroupMarketingAndSubscriptions() {
		this.form.controls.consents.controls.groupMarketing.reset(
			{ value: false, disabled: true },
			{ emitEvent: false }
		);
		this.form.controls.subscriptions.disable({ emitEvent: false });
	}

	/**
	 * Returns the `Label` property for the `GenericFormSubmit` action
	 *
	 * ```text
	 * 00 - neither accepted
	 * 01 - Betsson Updates accepted only
	 * 10 - Betsson Group Brands Updates accepted only
	 * 11 - both accepted
	 * ```
	 */
	private getGtmLabelOnSubmit(): "00" | "01" | "10" | "11" {
		const { brandMarketing, groupMarketing } = this.form.value.consents!;

		if (brandMarketing && groupMarketing) {
			return "11";
		}
		if (brandMarketing) {
			return "01";
		}
		if (groupMarketing) {
			return "10";
		}
		return "00";
	}
}
